import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import _ from "lodash";
import { requestTypes, status } from "../../utils/Helpers";
import { updateRequestStatus, deleteAdjustmentRequest } from "../../services";
import Popup from "../../components/SharedComponets/Popup";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Info,
  ChevronDown,
  Check,
  X,
  Clock,
  ShieldAlert,
  Activity,
  User,
  Trash2,
} from "lucide-react";

const ReqList = ({ requests, setAllRequests }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleOpenPopup = (content) => {
    setSelectedContent(content);
    setIsPopupOpen(true);
  };

  const handleUpdateStatus = async (Id, RequestStatus) => {
    try {
      setIsLoading(true);
      await updateRequestStatus({ Id, RequestStatus });
      setAllRequests((prev) => ({
        ...prev,
        RequestList: prev.RequestList.map((r) =>
          r.Id === Id ? { ...r, RequestStatus } : r
        ),
      }));
      setIsDropdownOpen(null);
      setIsModalOpen(false);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (Id) => {
    try {
      await deleteAdjustmentRequest(Id);
      setAllRequests((prev) => ({
        ...prev,
        TotalCount: prev.TotalCount - 1,
        RequestList: prev.RequestList.filter((r) => r.Id !== Id),
      }));
    } catch {
      // silent
    }
  };

  const openModal = (req) => {
    setSelectedRequest(req);
    setIsDropdownOpen(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const formatDate = (date, requestType) => {
    const d = moment(date);
    switch (requestType) {
      case "Leave":
      case "Breaks Adjustment":
        return d.format("DD MMM YYYY");
      case "CheckIn Adjustment":
      case "CheckOut Adjustment":
      case "Long Break":
      case "Overtime":
      case "Half Day":
        return d.format("DD MMM YYYY  h:mm A");
      default:
        return d.format("DD MMM YYYY");
    }
  };

  const formatCreatedAt = (date) =>
    moment(date).format("DD MMM YYYY [AT] h:mm A");

  const statusConfig = {
    1: { label: "Pending", color: "amber" },
    2: { label: "Rejected", color: "red" },
    3: { label: "Approved", color: "green" },
  };

  const colorMap = {
    amber: {
      badge: "bg-amber-500/10 text-amber-500 border-amber-500/40",
      dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]",
    },
    green: {
      badge: "bg-green-500/10 text-green-500 border-green-500/40",
      dot: "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]",
    },
    red: {
      badge: "bg-red-500/10 text-red-500 border-red-500/40",
      dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
    },
  };

  const hasRequests = !_.isEmpty(requests) && requests?.TotalCount > 0;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="space-y-3"
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-1 px-1">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            All Requests
          </h3>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Total: {requests?.TotalCount || 0}
          </span>
        </div>

        {/* Desktop List — Card per row */}
        <div className="hidden md:block bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 shadow-sm">
          {/* Column Headers */}
          <div className="grid grid-cols-[1.4fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_100px_60px] gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-800/60 bg-gray-50/60 dark:bg-gray-800/40">
            {["Member", "Request Type", "Start Date", "End Date", "Detail", "Created At", "Status", "Delete"].map((h, i) => (
              <div key={i} className={`text-xs font-semibold text-gray-400 uppercase tracking-widest ${h === "Delete" ? "text-right pr-2" : ""}`}>
                {h}
              </div>
            ))}
          </div>

          {/* Rows */}
          <div className="p-3 space-y-2">
            {hasRequests ? (
              <AnimatePresence mode="popLayout">
                {requests?.RequestList?.map((request, index) => {
                  const cfg = statusConfig[request.RequestStatus];
                  const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                  return (
                    <motion.div
                      key={request.Id || index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.03 } }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      className="grid grid-cols-[1.4fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_100px_60px] gap-4 items-center px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 rounded-xl border border-gray-100 dark:border-gray-800/40 transition-colors"
                    >
                      {/* Member */}
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-blue-500/10">
                          {request.Photopath ? (
                            <img
                              src={request.Photopath}
                              alt={request.MemberName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                          )}
                        </div>
                        <div className="text-[14px] font-light text-gray-900 dark:text-gray-100 truncate">
                          {request.MemberName}
                        </div>
                      </div>

                      {/* Request Type */}
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex-shrink-0">
                          <ClipboardList className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-[14px] font-light text-gray-700 dark:text-gray-300 truncate">
                          {requestTypes[request.RequestType]}
                        </div>
                      </div>

                      {/* Start Date */}
                      <div className="text-[14px] font-light text-gray-600 dark:text-gray-400 uppercase tracking-tight leading-relaxed">
                        {request.StartDate ? (
                          <>
                            <div>{moment(request.StartDate).format("DD MMM YY")}</div>
                            <div>{moment(request.StartDate).format("h:mm A")}</div>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>

                      {/* End Date */}
                      <div className="text-[14px] font-light text-gray-600 dark:text-gray-400 uppercase tracking-tight leading-relaxed">
                        {request.EndDate && !_.isNull(request.EndDate) ? (
                          <>
                            <div>{moment(request.EndDate).format("DD MMM YY")}</div>
                            <div>{moment(request.EndDate).format("h:mm A")}</div>
                          </>
                        ) : (
                          "—"
                        )}
                      </div>

                      {/* Detail — always show View Details */}
                      <div className="min-w-0">
                        {request.RequestDetail ? (
                          <button
                            onClick={() => handleOpenPopup(request.RequestDetail)}
                            className="text-[14px] font-light text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                          >
                            View Details
                          </button>
                        ) : (
                          <span className="text-[14px] font-light text-gray-400">—</span>
                        )}
                      </div>

                      {/* Created At */}
                      <div className="text-[14px] font-light text-gray-400 dark:text-gray-500 uppercase tracking-tight leading-relaxed">
                        <div>{moment(request.CreatedAt).format("DD MMM YY")}</div>
                        <div>{moment(request.CreatedAt).format("h:mm A")}</div>
                      </div>

                      {/* Status + Action */}
                      <div className="flex items-center gap-2 justify-end -mr-3">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[13px] font-light border uppercase tracking-[0.08em] whitespace-nowrap ${colors.badge}`}>
                          <span className={`w-1.5 h-1.5 rounded-full me-1.5 ${colors.dot}`} />
                          {cfg?.label ?? "Pending"}
                        </span>

                        {request.RequestStatus == 1 && (
                          <div className="relative" ref={dropdownRef}>
                            <button
                              onClick={() =>
                                setIsDropdownOpen(isDropdownOpen === index ? null : index)
                              }
                              className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>

                            <AnimatePresence>
                              {isDropdownOpen === index && (
                                <motion.div
                                  initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                  transition={{ duration: 0.12 }}
                                  className="absolute z-40 right-0 mt-2 w-36 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden"
                                >
                                  <button
                                    onClick={() => openModal({ Id: request.Id, RequestStatus: 3 })}
                                    className="flex items-center gap-2 w-full px-4 py-3 text-xs font-bold text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10 transition-colors"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => openModal({ Id: request.Id, RequestStatus: 2 })}
                                    className="flex items-center gap-2 w-full px-4 py-3 text-xs font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    Reject
                                  </button>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>

                      {/* Delete */}
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDelete(request.Id)}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all active:scale-90"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            ) : (
              <div className="py-20 flex flex-col items-center justify-center">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <Info className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-medium text-base">
                  No requests found.
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                  Try adjusting your filters.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="flex flex-col gap-3 md:hidden">
          {hasRequests ? (
            <AnimatePresence mode="popLayout">
              {requests?.RequestList?.map((request, index) => {
                const cfg = statusConfig[request.RequestStatus];
                const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                return (
                  <motion.div
                    key={request.Id || index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white dark:bg-gray-900/40 border border-gray-100 dark:border-blue-gray-900 rounded-2xl p-5 shadow-sm space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl overflow-hidden bg-blue-500/10 flex-shrink-0">
                          {request.Photopath ? (
                            <img
                              src={request.Photopath}
                              alt={request.MemberName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-4 h-4 text-blue-500" />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                            {request.MemberName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {requestTypes[request.RequestType]}
                          </div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase ${colors.badge}`}>
                        <span className={`w-1 h-1 rounded-full me-1.5 ${colors.dot}`} />
                        {cfg?.label ?? "Pending"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 py-3 border-y border-gray-50 dark:border-gray-800/50">
                      <div>
                        <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">Start</div>
                        <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase">
                          {request.StartDate ? formatDate(request.StartDate, requestTypes[request.RequestType]) : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">End</div>
                        <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase">
                          {request.EndDate ? formatDate(request.EndDate, requestTypes[request.RequestType]) : "—"}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-[10px] text-gray-400 uppercase">
                        <Clock className="w-3 h-3 inline me-1" />
                        {formatCreatedAt(request.CreatedAt)}
                      </div>
                      {request.RequestStatus == 1 && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal({ Id: request.Id, RequestStatus: 3 })}
                            className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-green-500/10 text-green-600 border border-green-500/30 hover:bg-green-500/20 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => openModal({ Id: request.Id, RequestStatus: 2 })}
                            className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          ) : (
            <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 p-10 text-center">
              <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No requests found.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Detail Popup */}
      {isPopupOpen && (
        <Popup content={selectedContent} onClose={() => setIsPopupOpen(false)} />
      )}

      {/* Approve / Reject Confirmation Modal */}
      <AnimatePresence>
        {isModalOpen && selectedRequest && (
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[100]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-blue-gray-900 overflow-hidden"
            >
              <div className="px-8 pt-8 pb-4 flex flex-col items-center text-center">
                <div className={`p-4 rounded-2xl mb-4 ${selectedRequest.RequestStatus === 3 ? "bg-green-500/10" : "bg-red-500/10"}`}>
                  {selectedRequest.RequestStatus === 3 ? (
                    <Check className="w-7 h-7 text-green-500" />
                  ) : (
                    <ShieldAlert className="w-7 h-7 text-red-500" />
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedRequest.RequestStatus === 3 ? "Approve Request?" : "Reject Request?"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {selectedRequest.RequestStatus === 3
                    ? "This request will be marked as approved."
                    : "This request will be marked as rejected."}
                </p>
              </div>

              <div className="px-8 pb-8 pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  disabled={isLoading}
                  onClick={() => handleUpdateStatus(selectedRequest.Id, selectedRequest.RequestStatus)}
                  className={`flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-70 flex items-center justify-center gap-2 ${selectedRequest.RequestStatus === 3
                    ? "bg-green-600 hover:bg-green-700 shadow-lg shadow-green-500/20"
                    : "bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/20"
                    }`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : selectedRequest.RequestStatus === 3 ? (
                    <><Check className="w-4 h-4" /> Approve</>
                  ) : (
                    <><X className="w-4 h-4" /> Reject</>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ReqList;
